/**
 * @module utils/linkify
 *
 * Split plain text into text and link segments so http(s) URLs can render as
 * clickable links WITHOUT v-html (a description can never inject markup —
 * each segment is rendered as text or as an <a> by the template). Used by the
 * episode info dialog (EpisodeContextMenu.vue) and the players' episode info
 * sheet (EpisodeInfoSheet.vue).
 */

/**
 * linkifySegments - Split text into `{ text }` and `{ text, href }` segments.
 *
 * Trailing sentence punctuation is left OUT of the link ("…see
 * https://x.it/a." links to https://x.it/a). A closing bracket is only
 * trimmed when the URL has no opening one, so a Wikipedia-style
 * ".../Foo_(bar)" keeps its ")". Joining every segment's `text` gives back
 * the original string exactly.
 *
 * @param {string} [text]
 * @returns {Array<{text: string, href?: string}>}
 */
export function linkifySegments(text) {
  const source = text || "";
  const segments = [];
  let last = 0;
  for (const match of source.matchAll(/https?:\/\/[^\s<>"]+/gi)) {
    let url = match[0].replace(/[.,;:!?'"»”’]+$/u, "");
    if (url.endsWith(")") && !url.includes("(")) url = url.replace(/\)+$/, "");
    if (url.endsWith("]") && !url.includes("[")) url = url.replace(/\]+$/, "");
    // Text before the link, then the link itself; whatever was trimmed off
    // the end stays in the text that follows.
    if (match.index > last) segments.push({ text: source.slice(last, match.index) });
    segments.push({ text: url, href: url });
    last = match.index + url.length;
  }
  if (last < source.length) segments.push({ text: source.slice(last) });
  return segments;
}
